import { type DjangoWaffleType } from "@shared/contexts/WaffleContext/django-waffle-type";
import { URL_MAP, URLMapEntry } from "./constants";

// These values will be replaced at build time with the actual environment values
declare const process: {
  env: {
    SITE_URL: string;
    APP_ENV: string;
  };
};

export class ConfigService {
  host: string;
  environment: string;
  waffleReady: Promise<boolean> | undefined;

  constructor() {
    this.host = process.env.SITE_URL.replace(/\/$/, "");
    this.environment = process.env.APP_ENV;

    if (!window.waffle) {
      // Preserve the public SDK flag() contract even though calendar routing no longer reads waffle here.
      this.loadWaffle();
    }
  }

  private loadWaffle = () => {
    this.waffleReady = new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = `${this.host}/wafflejs`;
      if (this.environment !== "local") {
        script.crossOrigin = "use-credentials";
      }
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.head.appendChild(script);
    });
  };

  private getUrl = async <TParams extends readonly unknown[]>(
    entry: URLMapEntry<TParams>,
    ...params: TParams
  ) => {
    return `${this.host}${entry.path(...params)}`;
  };

  loginWidgetUrl = () => this.getUrl(URL_MAP.login);

  checkoutWidgetUrl = () => this.getUrl(URL_MAP.checkout);

  productPurchaseWidgetUrl = (id: string) =>
    this.getUrl(URL_MAP.productPurchase, id);

  expressCheckoutWidgetUrl = () => this.getUrl(URL_MAP.expressCheckout);

  eventPurchaseWidgetUrl = (slug: string) =>
    this.getUrl(URL_MAP.eventPurchase, slug);

  flag = (flagName: keyof DjangoWaffleType["FLAGS"]) =>
    window.waffle?.FLAGS?.[flagName];

  calendarWidgetUrl = async (
    venueIdOrSlug: string | number,
    isAttraction = false,
    eventId?: string
  ) => {
    const venueIdOrSlugString = venueIdOrSlug.toString();
    const attractionEventId = eventId?.trim();

    const shouldUseNewAttractionCalendar =
      isAttraction && Boolean(attractionEventId);

    if (shouldUseNewAttractionCalendar) {
      return `${this.host}/widget/calendar/attraction/${attractionEventId}`;
    }

    if (isAttraction) {
      return this.getUrl(URL_MAP.calendarLegacy, venueIdOrSlugString);
    }

    return this.getUrl(URL_MAP.calendar, venueIdOrSlugString);
  };

  basketWidgetUrl = () => this.getUrl(URL_MAP.basket);

  membershipPurchaseWidgetUrl = (id: string) =>
    this.getUrl(URL_MAP.membership, id);
}
